/*
 * Orders — `orders.list` + expanded order card (App-shell canon §7.1,
 * FLEXIBLE). Transcribed from the canon §7.1 tree + §7.2 testid table + the
 * DC source (App Shell.dc.html ordersView). Canon wins on conflict.
 *
 * ROUTES (canon §1.2): /orders (list) · /orders/:orderRef (expanded card) —
 * the caret toggles between them. Ticket rows open
 * /orders/:orderRef/tickets/:ticketId (TicketDetail.tsx, §7.2).
 *
 * DATA (ruling A, BOARDROOM-2026-08-09-133534): ONLY live data — box-office
 * orders (DataProvider) + my-api allocation holders merged into ruling-B
 * seats[] (useOrderSeats; the my-api holder is allocation-authoritative,
 * e75108d). The canon [demo] order lives in src/data/seed.ts as a TEST
 * FIXTURE only. While the allocation service is unreadable the card renders
 * its real box-office header + an honest notice — counts are never invented.
 *
 * STATE COLOURS (§7.1 [token], boardroom ruling — fixes the pre-canon
 * inversion): amber `--warn` = seats unassigned · blue `--info-invited` =
 * invite pending · teal `--accent` = done.
 */
import { useState, type CSSProperties } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useShellChrome } from '../components/AppShell'
import { startSignIn } from '../auth/startSignIn'
import { LiveDataNotice, useData } from '../data/DataProvider'
import type { Money, Order, Ticket } from '../data/types'
import { orderSummary, ticketAssignment, type TicketDotState } from './orders/ordersModel'
import { useOrderSeats, type AllocState } from './orders/useOrderSeats'

const money = (m: Money) => `${m.amount.toLocaleString()} ${m.currency}`

// ---------------------------------------------------------------------------
// Tokens per dot state (§7.1: dot + matching tint glow ring)
// ---------------------------------------------------------------------------

const DOT: Record<TicketDotState, { fg: string; ring: string }> = {
  need: { fg: 'var(--warn)', ring: 'rgba(230,193,90,.16)' },
  pending: { fg: 'var(--info-invited)', ring: 'var(--tint-invited)' },
  done: { fg: 'var(--accent)', ring: 'rgba(18,196,172,.16)' },
}

/**
 * Group an order's child tickets into team blocks (BOARDROOM-2026-08-13-222526).
 * Pure — unit-tested. Groups by `typeName` in FIRST-SEEN order so a Teams-of-N
 * purchase reads as one block ("Teams of 3 · Male" with its N members) instead
 * of a flat interleaved list.
 *
 * HEURISTIC (named limitation): box-office's /consumer/me/orders wire carries no
 * ticket_unit_id / member_seq (the D-gap escalated to tickets), so we can only
 * group by the composed typeName. Correct for the common one-purchase-per-team-
 * type case; the single imperfection is that two SEPARATE same-type units in one
 * order collapse into one block — still strictly better than the flat interleave,
 * and fully resolved the moment box-office exposes a real unit id (swap the key).
 * Order of groups + members within a group is preserved (stable).
 */
export function groupTicketsByType(tickets: Ticket[]): Array<{ typeName: string; tickets: Ticket[] }> {
  const order: string[] = []
  const byType = new Map<string, Ticket[]>()
  for (const t of tickets) {
    const key = t.typeName || 'Ticket'
    if (!byType.has(key)) { byType.set(key, []); order.push(key) }
    byType.get(key)!.push(t)
  }
  return order.map((typeName) => ({ typeName, tickets: byType.get(typeName)! }))
}

/**
 * CMT-2026-08-14-035 (revised per CMT-2026-08-14-038 t5) — per-group card colour.
 * Duncan: the first cut hashed the typeName to an 8-hue palette, but two of the
 * hues are reddish (pink + rose) so his two ticket-types both read as "red". The
 * point is CLEAR DISTINCTNESS between adjacent cards, not a stable per-type hue.
 * So assign by GROUP INDEX from a palette ORDERED for maximum adjacent contrast,
 * leading green → amber (Duncan's own example "one green, one amber"). Within a
 * single order the groups are already distinct typeNames (groupTicketsByType
 * dedupes), so index assignment guarantees neighbouring cards never share a hue.
 * Fixed hex (theme-var-free) so the accent reads identically in light + dark.
 */
const GROUP_ACCENTS = [
  '#22c55e', // green
  '#f59e0b', // amber
  '#3b82f6', // blue
  '#a855f7', // purple
  '#14b8a6', // teal
  '#ec4899', // pink
  '#f97316', // orange
  '#ef4444', // red
] as const

export function groupAccent(index: number): string {
  // Deterministic by position; wraps for orders with more than 8 groups. No
  // Math.random → stable snapshots + stable visual identity within a render.
  const i = Number.isFinite(index) && index >= 0 ? Math.floor(index) : 0
  return GROUP_ACCENTS[i % GROUP_ACCENTS.length]
}

// ---------------------------------------------------------------------------
// §7.1 order card (presentational — tests render it with the seed fixture)
// ---------------------------------------------------------------------------

export interface OrderCardData {
  ref: string
  event: string
  date: string
  total: string
  tickets: Ticket[]
  /** Real invoice artefact from box-office (D12). When absent the link hides —
   *  never a dead placeholder. */
  invoiceUrl?: string
}

/** Small teal "Invoice ›" link under the total (canon §7.1). Renders ONLY when
 *  a real invoiceUrl exists (D12) — opens the artefact in a new tab; hidden when
 *  absent (no dead toast). */
function InvoiceLink({ href }: { href?: string }) {
  if (!href) return null
  return (
    <a
      data-testid="orders-invoice"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ display: 'inline-block', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, color: 'var(--accent-bright)', textDecoration: 'none', cursor: 'pointer' }}
    >Invoice ›</a>
  )
}

export function OrderCard({ data, expanded, onToggle, onOpenTicket }: {
  data: OrderCardData
  expanded: boolean
  onToggle?: () => void
  onOpenTicket?: (ticketId: string, assignSeatNo?: number) => void
}) {
  const { isMobile } = useShellChrome()
  const s = orderSummary(data.tickets)

  return (
    <div
      id={`orders.card.${data.ref}`}
      data-testid="orders-card"
      data-order-ref={data.ref}
      style={{ background: 'var(--surface)', border: '1px solid var(--hairline-08)', borderRadius: 'var(--r-order-card, 16px)', overflow: 'hidden', marginBottom: 16 }}
    >
      {/* header row: caret · mono Order ref · meta · total + Invoice › */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '15px 16px', borderBottom: '1px solid var(--hairline-07)', background: 'var(--bg-frame)' }}>
        <button
          data-testid="orders-card-toggle"
          aria-expanded={expanded}
          onClick={onToggle}
          style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1, minWidth: 0, border: 0, background: 'transparent', fontFamily: 'inherit', textAlign: 'left', padding: 0, cursor: 'pointer', color: 'inherit' }}
        >
          <span aria-hidden style={{ flex: 'none', color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>{expanded ? '▾' : '▸'}</span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span data-testid="orders-ref" className="mono" style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', letterSpacing: '.05em' }}>{`Order ${data.ref}`}</span>
            {/* meta: `{event} · {date}` inline on desktop / stacked on mobile (§7.1) */}
            {isMobile ? (
              <>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginTop: 3, lineHeight: 1.35 }}>{data.event}</span>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--text-faint)' }}>{data.date}</span>
              </>
            ) : (
              <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>{`${data.event} · ${data.date}`}</span>
            )}
          </span>
        </button>
        <div style={{ textAlign: 'right', flex: 'none' }}>
          <div data-testid="orders-total" style={{ fontSize: 15, fontWeight: 800, whiteSpace: 'nowrap' }}>{data.total}</div>
          {/* D12: real invoice artefact (order.invoiceUrl) → open it; hidden when
              box-office has not supplied one (no dead placeholder). */}
          <InvoiceLink href={data.invoiceUrl} />
        </div>
      </div>

      {/* assignment summary + progress */}
      <div style={{ padding: '10px 16px' }}>
        <div data-testid="orders-summary" style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 7, whiteSpace: 'nowrap', overflow: 'hidden' }}>
          <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--text)' }}>{`${s.ticketCount} tickets`}</span>
          {s.needTickets > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 700, color: 'var(--warn)' }}>
              <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--warn)' }} />
              {isMobile ? `${s.needTickets} need` : `${s.needTickets} need action`}
            </span>
          )}
          {s.doneTickets > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 700, color: 'var(--success)' }}>
              <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)' }} />
              {isMobile ? `${s.doneTickets} done` : `${s.doneTickets} complete`}
            </span>
          )}
          <span style={{ flex: 1 }} />
          {/* `{d} of {t} people assigned` — desktop only (§7.1 mobile AC) */}
          {!isMobile && (
            <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>{`${s.assigned} of ${s.total} people assigned`}</span>
          )}
        </div>
        <div style={{ height: 5, borderRadius: 20, background: 'var(--progress-track, rgba(255,255,255,.08))', overflow: 'hidden' }}>
          <div data-testid="orders-progress" style={{ height: '100%', borderRadius: 20, background: 'var(--accent)', width: `${s.pct}%` }} />
        </div>
      </div>

      {/* indented ticket list (left rail) */}
      {expanded && (
        <div id={`orders.ticketlist.${data.ref}`} style={{ margin: '0 0 0 22px', borderLeft: '2px solid rgba(255,255,255,.09)' }}>
          <div className="mono" style={{ fontSize: 9.5, letterSpacing: '.1em', color: 'var(--text-dim)', padding: '11px 14px 3px' }}>
            TICKETS IN THIS ORDER
          </div>
          {/* TEAM GROUPING (BOARDROOM-2026-08-13-222526 / -095425): child tickets
              of a team read as one banded block instead of a flat interleaved
              list, so "this is the team of 3, this is the team of 2" is legible.
              HEURISTIC — box-office's /consumer/me/orders wire carries NO
              ticket_unit_id / member_seq (D-gap escalated), so we group by
              typeName within the order. This is correct for the common case
              (one purchase per team-type); its ONE imperfection is that two
              SEPARATE same-type units in one order merge into a single block —
              still strictly better than the flat interleave, and resolved fully
              when box-office exposes ticket_unit_id. A single-member group (a
              non-team ticket) renders with no header, unchanged. */}
          {/* CMT-2026-08-14-035: EVERY group — team OR individual — is now its own
              card with the site highlight-line theme: a per-group colour running
              ACROSS THE TOP and DOWN THE LEFT edge, a header on every card (the
              lone individual no longer renders headerless + breaks the layout),
              and a short connector line from the left rail to each ticket row.
              Colour is deterministic per typeName (groupAccent) so a given team-
              type reads with one stable hue. */}
          {groupTicketsByType(data.tickets).map((group, groupIndex) => {
            const accent = groupAccent(groupIndex)
            const memberLabel = `${group.tickets.length} ${group.tickets.length === 1 ? 'MEMBER' : 'MEMBERS'}`
            return (
              <div
                key={group.typeName}
                data-testid="orders-ticket-group"
                data-group-type={group.typeName}
                data-group-accent={accent}
                style={{
                  margin: '10px 8px 0',
                  borderRadius: 10,
                  overflow: 'hidden',
                  // The highlight line: bright accent across the TOP + down the
                  // LEFT; the other two edges stay a quiet hairline so the L-shape
                  // reads as the design's "line across the top and down the side".
                  borderTop: `2px solid ${accent}`,
                  borderLeft: `3px solid ${accent}`,
                  borderRight: '1px solid var(--hairline-12)',
                  borderBottom: '1px solid var(--hairline-12)',
                }}
              >
                <div
                  data-testid="orders-group-header"
                  style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '9px 14px', background: `color-mix(in srgb, ${accent} 12%, transparent)`, borderBottom: '1px solid var(--hairline-06)' }}
                >
                  {/* colour chip reinforces the per-card hue at the label */}
                  <span aria-hidden style={{ width: 8, height: 8, flex: 'none', borderRadius: 2, background: accent }} />
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--text)' }}>{group.typeName}</span>
                  <span className="mono" style={{ fontSize: 9.5, letterSpacing: '.08em', color: 'var(--text-dim)' }}>
                    {memberLabel}
                  </span>
                </div>
                {group.tickets.map((t) => {
                  const a = ticketAssignment(t)
                  const dot = DOT[a.dot]
                  // CMT-2026-08-14-038 t4: a ticket that still needs assigning
                  // routes STRAIGHT to the assign step for its first unassigned
                  // seat — skipping the tautological "Please assign this ticket"
                  // confirm page. An already-assigned ticket still opens its
                  // manage/detail view (seatNo undefined).
                  const assignSeatNo =
                    a.dot === 'need' ? t.seats.find((s) => s.status === 'unassigned')?.seatNo : undefined
                  return (
                    <button
                      key={t.id}
                      data-testid="orders-ticket-row"
                      data-ticket-id={t.id}
                      onClick={() => onOpenTicket?.(t.id, assignSeatNo)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', minHeight: 44, border: 0, borderTop: '1px solid var(--hairline-06)', background: 'transparent', fontFamily: 'inherit', textAlign: 'left', padding: '12px 14px 12px 10px', cursor: 'pointer', color: 'inherit' }}
                    >
                      {/* connector: a short accent line branching from the card's
                          left rail to this ticket (the design's "line to each
                          ticket within"). */}
                      <span aria-hidden style={{ width: 10, height: 2, flex: 'none', borderRadius: 2, background: accent, opacity: 0.7 }} />
                      <span
                        data-testid="orders-ticket-dot"
                        data-state={a.dot}
                        aria-hidden
                        style={{ width: 9, height: 9, flex: 'none', borderRadius: '50%', background: dot.fg, boxShadow: `0 0 0 3px ${dot.ring}` }}
                      />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        {/* Row label = "{Type} · {Gender}" (box-office composes it
                            into typeName). In a team block the header already
                            states the type, but each member row keeps the full
                            label for scanning + deep-link parity. */}
                        <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{t.typeName}</span>
                        <span
                          data-testid="orders-ticket-sub"
                          style={{
                            display: 'block', fontSize: 12, marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            ...(a.dot === 'need'
                              ? { color: 'var(--warn)', fontWeight: 700 }
                              : a.dot === 'pending'
                                ? { color: 'var(--info-invited)', fontWeight: 700 }
                                : { color: 'var(--text-muted)' }),
                          }}
                        >{a.sub}</span>
                      </span>
                      {a.done && (
                        <span aria-hidden style={{ flex: 'none', fontSize: 15, fontWeight: 800, color: dot.fg }}>{a.tickGlyph}</span>
                      )}
                      {/* CMT-2026-08-14-038 t3: a needs-assigning row now shows an
                          obvious ACTION pill ("Assign now ›") instead of a passive
                          chevron, so it plainly reads as a tap target. Assigned /
                          pending rows keep the quiet chevron. */}
                      {a.dot === 'need' ? (
                        <span
                          data-testid="orders-assign-cta"
                          style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 800, color: 'var(--accent-ink)', background: 'var(--accent)', borderRadius: 20, padding: '5px 11px', whiteSpace: 'nowrap' }}
                        >Assign now ›</span>
                      ) : (
                        <span aria-hidden style={{ flex: 'none', color: 'var(--text-chevron)', fontSize: 16 }}>›</span>
                      )}
                    </button>
                  )
                })}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Live card wrapper — allocation-merged seats per order; honest states
// ---------------------------------------------------------------------------

/** Honest in-card notice while allocation state is unreadable (ruling A + D11):
 *  the header (real box-office data) renders; counts are never invented. Copy
 *  is state-accurate — a signed-out session is NEVER blamed on the build. */
export function AllocNotice({ state, style }: {
  state: Exclude<AllocState, 'ready'>
  /** Optional container-style override (TicketDetail uses a top-margin form). */
  style?: CSSProperties
}) {
  const base: CSSProperties = { padding: '12px 16px', fontSize: 11.5, color: 'var(--text-faint)', lineHeight: 1.5, ...style }
  if (state === 'signed-out') {
    return (
      <p role="status" data-wired="false" data-alloc-state="signed-out" style={base}>
        Please sign in to see your seat assignments.{' '}
        <button
          data-testid="orders-signin"
          onClick={() => { void startSignIn() }}
          style={{ border: 0, background: 'transparent', padding: 0, fontFamily: 'inherit', fontSize: 11.5, fontWeight: 700, color: 'var(--accent-bright)', cursor: 'pointer', textDecoration: 'underline' }}
        >Sign in</button>
      </p>
    )
  }
  const msg =
    state === 'loading'
      ? 'Loading seat assignments…'
      : state === 'unavailable'
        ? 'The allocation service is not responding — seat assignments will appear when it is back.'
        : 'The allocation service (my-api) is not connected in this build — seat assignments appear the moment it is.'
  return <p role="status" data-wired="false" data-alloc-state={state} style={base}>{msg}</p>
}

function LiveOrderCard({ order, expanded, onToggle }: {
  order: Order
  expanded: boolean
  onToggle: () => void
}) {
  const { eventById } = useData()
  const navigate = useNavigate()
  const { state, tickets } = useOrderSeats(order)
  const ev = eventById(order.eventId)
  const data: OrderCardData = {
    ref: order.ref,
    event: ev?.name ?? 'Your event',
    date: order.purchasedAt,
    total: money(order.total),
    tickets,
    invoiceUrl: order.invoiceUrl,
  }
  if (state !== 'ready') {
    // Header only + honest notice — no invented summary/seat states.
    return (
      <div
        id={`orders.card.${data.ref}`}
        data-testid="orders-card"
        data-order-ref={data.ref}
        style={{ background: 'var(--surface)', border: '1px solid var(--hairline-08)', borderRadius: 16, overflow: 'hidden', marginBottom: 16 }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '15px 16px', borderBottom: '1px solid var(--hairline-07)', background: 'var(--bg-frame)' }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span data-testid="orders-ref" className="mono" style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.05em' }}>{`Order ${data.ref}`}</span>
            <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>{`${data.event} · ${data.date}`}</span>
          </span>
          <div style={{ textAlign: 'right', flex: 'none' }}>
            <div data-testid="orders-total" style={{ fontSize: 15, fontWeight: 800, whiteSpace: 'nowrap' }}>{data.total}</div>
            <InvoiceLink href={data.invoiceUrl} />
          </div>
        </div>
        <AllocNotice state={state} />
      </div>
    )
  }
  return (
    <OrderCard
      data={data}
      expanded={expanded}
      onToggle={onToggle}
      onOpenTicket={(ticketId, assignSeatNo) =>
        navigate(
          // CMT-038 t4: needs-assigning → straight into the assign step (skip the
          // intermediate confirm page); otherwise open the ticket detail/manage view.
          assignSeatNo != null
            ? `/orders/${order.ref}/tickets/${ticketId}/seats/${assignSeatNo}/assign`
            : `/orders/${order.ref}/tickets/${ticketId}`,
        )
      }
    />
  )
}

// ---------------------------------------------------------------------------
// Screen — `orders.root` (routes /orders + /orders/:orderRef)
// ---------------------------------------------------------------------------

export function Orders() {
  const { liveState, orders } = useData()
  const { showPageHead } = useShellChrome()
  const { orderRef } = useParams()
  // D9 — canon boots EVERY card expanded so "Tap a ticket to assign or manage
  // it." never sits above a screen of collapsed code-led headers. The caret
  // collapses a card locally (route-driven single-expand was the divergence);
  // a deep-linked /orders/:orderRef keeps that card expanded regardless.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const toggle = (ref: string) => setCollapsed((c) => ({ ...c, [ref]: !c[ref] }))

  return (
    <div id="orders.root" style={{ maxWidth: 'var(--content-max, 700px)' }}>
      {/* Desktop-only page head (§7.1; mobile hides page h1s, §3.2) */}
      {showPageHead && (
        <div id="orders.head" data-testid="orders-head">
          <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 4px' }}>Orders</h1>
          {/* Verbatim [chrome] sub (canon §7.1). */}
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 16px' }}>Tap a ticket to assign or manage it.</p>
        </div>
      )}
      {liveState !== 'ready' ? (
        <LiveDataNotice liveState={liveState} />
      ) : orders.length === 0 ? (
        // Honest empty state (ruling A — copy is TBD-UNSEEN in the canon).
        <p data-testid="orders-empty" className="secondary" style={{ fontSize: 13 }}>
          No orders are connected to this account yet. Orders you place appear
          here with every ticket and place inside them.
        </p>
      ) : (
        orders.map((o) => (
          <LiveOrderCard
            key={o.id}
            order={o}
            // Boot expanded (canon); a deep-linked orderRef forces its card open
            // even if previously collapsed. Otherwise the caret's local state wins.
            expanded={orderRef === o.ref ? true : !collapsed[o.ref]}
            onToggle={() => toggle(o.ref)}
          />
        ))
      )}
    </div>
  )
}
